import React, { useEffect, useState } from "react";
import API from "../../api/api";
import ChatContainer from "../Chat/ChatContainer";

type Match = {
  doc: {
    name: string;
    id: string;
    fullName: string;
    avatar: string;
    birthCity: string;
    birthDistrict?: string;
    birthCountry?: string;
    refugeeStatus?: string;
    motherName?: string;
    familyClan?: string;
    religiousCustoms?: string;
    hereditaryConditions?: string;
    previousTracingEfforts?: string;
    age?: number;
    email?: string;
  };
  score: number;
  reasons?: string[];
};

interface MatchResponse {
  topMatches: Match[];
}

const MatchList: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | any>("");
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showChat, setShowChat] = useState<boolean>(false);
  const [selectedMatchForChat, setSelectedMatchForChat] = useState<Match | null>(null);

  // Function to get field value for display
  const getFieldValue = (match: Match, reason: string) => {
    const reasonLower = reason.toLowerCase();
    if (reasonLower.includes('first name')) {
      return match.doc.fullName?.split(' ')[0] || 'N/A';
    }
    if (reasonLower.includes('last name')) {
      const names = match.doc.fullName?.split(' ') || [];
      return names[names.length - 1] || 'N/A';
    }
    if (reasonLower.includes('birth city')) {
      return match.doc.birthCity || 'N/A';
    }
    if (reasonLower.includes('birth district')) {
      return match.doc.birthDistrict || 'N/A';
    }
    if (reasonLower.includes('birth country')) {
      return match.doc.birthCountry || 'N/A';
    }
    if (reasonLower.includes('refugee status')) {
      return match.doc.refugeeStatus || 'N/A';
    }
    if (reasonLower.includes('mother')) {
      return match.doc.motherName || 'N/A';
    }
    if (reasonLower.includes('family clan')) {
      return match.doc.familyClan || 'N/A';
    }
    if (reasonLower.includes('religious customs')) {
      return match.doc.religiousCustoms || 'N/A';
    }
    if (reasonLower.includes('hereditary conditions')) {
      return match.doc.hereditaryConditions || 'N/A';
    }
    if (reasonLower.includes('previous tracing')) {
      return match.doc.previousTracingEfforts || 'N/A';
    }
    if (reasonLower.includes('age')) {
      return match.doc.age ? `${match.doc.age} years old` : 'Their age difference is within 5 years, making them close in age range.';
    }

    return null; // No specific value to display
  };

  // Function to get detailed explanation for age matching
  const getAgeExplanation = (match: Match, reason: string) => {
    const reasonLower = reason.toLowerCase();
    if (reasonLower.includes('age') && reasonLower.includes('±5')) {
      if (match.doc.age) {
        return `Their age difference is within 5 years, making them close in age range.`;
      }
    }
    return null;
  };

  // Default reasons array
  const defaultReasons = [
    "First name matches.",
    "Last name matches.",
    "Birth city matches.",
    "Birth district matches.",
    "Birth country matches.",
    "Refugee status matches.",
    "Mother's name matches.",
    "Family clan matches.",
    "Religious customs match (fuzzy).",
    "Hereditary conditions match (fuzzy).",
    "Previous tracing efforts match (fuzzy).",
    "Age matches (within ±5 years)."
  ];

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const response = await API.get<MatchResponse>("/match");
        setMatches(response.data.topMatches);
      } catch (err: any) {
        console.error("Failed to fetch matches:", err.response.data.message);
        setError(err.response.data.message || "Something went wrong while fetching matches.");
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, []);

  const handleViewClick = (match: Match) => {
    console.log("handleViewClick", match);
    // Debug: Log the full match object to see what data we have
    console.log('Full match object:', match);
    console.log('Match doc:', match?.doc);
    console.log('Match doc name:', match?.doc?.name);
    console.log('Match doc fullName:', match?.doc?.fullName);

    // Validate match data before setting
    if (!match || !match.doc) {
      console.error('Invalid match data - missing match or doc:', match);
      alert('Unable to start chat. Invalid match data.');
      return;
    }

    // Check if we have a valid identifier (name or fullName)
    if (!match.doc.name && !match.doc.fullName) {
      console.error('Invalid match data - missing name and fullName:', match.doc);
      alert('Unable to start chat. Missing user information.');
      return;
    }

    console.log('Starting chat with match:', match);
    setSelectedMatchForChat(match);
    setShowChat(true);
  };

  const handleCloseChat = () => {
    setShowChat(false);
    setSelectedMatchForChat(null);
  };

  const handleShowDetails = (match: Match) => {
    setSelectedMatch(match);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMatch(null);
  };


  return (
    <>
      <section className="vh-100" style={{ backgroundColor: "#eee" }}>
        <div className="container py-4 h-100">
          <div className="row d-flex justify-content-center h-100">
            <div className="col-md-12 col-xl-12">
              <div className="card">
                <div className="card-header p-3">
                  <h5 className="mb-0">
                    <i className="fas fa-tasks me-2"></i>Latest Potential Matches
                  </h5>
                </div>
                <div
                  className="card-body"
                  data-mdb-perfect-scrollbar="true"
                  style={{ position: "relative", height: "400px" }}
                >
                  {loading && <p>Loading...</p>}

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                      {error === "Please fill out the form first to be eligible for matching." && (
                        <div className="mt-2">
                          <a href="/family-match" className="btn btn-primary">
                            Fill out the questionnaire to find matches or get a chance to be matched.
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {!loading && !error && (
                    <table className="table mb-0">
                      <thead>
                        <tr>
                          <th scope="col">Full names</th>
                          <th scope="col">Birth City</th>
                          <th scope="col">Percentage</th>
                          <th scope="col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matches?.map((match, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <img
                                  src={match.doc.avatar || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRPoAJ0jJpb24AFIkHZ4RfZTXFAHLLjxm33Fg&s"}
                                  alt=""
                                  style={{ width: "45px", height: "45px" }}
                                  className="rounded-circle"
                                />
                                <div className="ms-3">
                                  <p className="fw-bold mb-1">{match.doc.fullName}</p>
                                </div>
                              </div>
                            </td>
                            <td>{match.doc.birthCity}</td>
                            <td>
                              <span className="badge bg-success rounded-pill">
                                {match.score.toFixed(2)}%
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-primary btn-sm me-2"
                                onClick={() => handleViewClick(match)}
                              >
                                <i className="fas fa-comment-dots me-1"></i>
                                Chat
                              </button>
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => handleShowDetails(match)}
                              >
                                <i className="fas fa-info-circle me-1"></i>
                                Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && selectedMatch && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-lg" style={{ zIndex: 10000 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-info-circle me-2"></i>
                  Match Details - {selectedMatch.doc.fullName}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-3 text-center">
                    <img
                      src={selectedMatch.doc.avatar || "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRPoAJ0jJpb24AFIkHZ4RfZTXFAHLLjxm33Fg&s"}
                      alt={selectedMatch.doc.fullName}
                      style={{ width: "80px", height: "80px" }}
                      className="rounded-circle mb-2"
                    />
                    <h6>{selectedMatch.doc.fullName}</h6>
                    <p className="text-muted small">{selectedMatch.doc.birthCity}</p>
                  </div>
                  <div className="col-md-9">
                    <div className="d-flex align-items-center mb-3">
                      <span className="badge bg-success fs-6 me-3">
                        {selectedMatch.score.toFixed(2)}% Match
                      </span>
                    </div>
                    <h6 className="mb-3">
                      <i className="fas fa-list me-2"></i>
                      Matching Reasons:
                    </h6>
                    <ul className="list-group list-group-flush">
                      {(selectedMatch.reasons || defaultReasons).map((reason, index) => {
                        const fieldValue = getFieldValue(selectedMatch, reason);
                        const ageExplanation = getAgeExplanation(selectedMatch, reason);
                        return (
                          <li key={index} className="list-group-item">
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <i className="fas fa-check-circle text-success me-2"></i>
                                <span>{reason}</span>
                              </div>
                              {fieldValue && (
                                <span className="badge bg-light text-dark ms-2">
                                  {fieldValue}
                                </span>
                              )}
                            </div>
                            {ageExplanation && (
                              <div className="mt-2 ms-4">
                                <small className="text-muted">
                                  <i className="fas fa-info-circle me-1"></i>
                                  {ageExplanation}
                                </small>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
                <button type="button" className="btn btn-primary">
                  Contact Match
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Container */}
      {showChat && (
        <ChatContainer
          onClose={handleCloseChat}
          matchedUser={selectedMatchForChat && selectedMatchForChat.doc ? {
            name: selectedMatchForChat.doc.name || selectedMatchForChat.doc.fullName || 'unknown_user',
            fullName: selectedMatchForChat.doc.fullName || 'Unknown User',
            avatar: selectedMatchForChat.doc.avatar,
            matchedUserId: selectedMatchForChat.doc.id,
            birthCity: selectedMatchForChat.doc.birthCity || '',
            email: selectedMatchForChat.doc.email
          } : undefined}
        />
      )}
    </>
  );
};

export default MatchList;